import { useCallback, useEffect, useState } from 'react';
import type {
  AdminListResponse,
  AdminOrganizationRow,
} from '../../../domain/superadmin/types';
import {
  EMPTY_ORG_FILTERS,
  type AdminOrganizationFilters,
} from '../../../domain/superadmin/organizationFilters';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminOrganizations } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;

export function useAdminOrganizations() {
  const [data, setData] = useState<AdminListResponse<AdminOrganizationRow> | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<AdminOrganizationFilters>(EMPTY_ORG_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminOrganizations({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        plan: filters.plan || undefined,
        statusBucket: filters.statusBucket || undefined,
        sort: filters.sort !== 'recent' ? filters.sort : undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar empresas');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, filters, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchFilters = useCallback((patch: Partial<AdminOrganizationFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  return {
    data,
    isLoading,
    error,
    refetch: load,
    page,
    setPage,
    q,
    setQ,
    filters,
    patchFilters,
    pageSize: PAGE_SIZE,
  };
}
